import {
  ContentConsentTypeEnum,
  ProgrammeTypeDto,
  ClassroomDto,
  SiteAddressDto,
  ProvinceDto,
} from '@ecdlink/core';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Button,
  ButtonGroup,
  Typography,
  ButtonGroupTypes,
  renderIcon,
  Checkbox,
  ImageInput,
  Dialog,
  DialogPosition,
  FormInput,
  Dropdown,
  classNames,
  IMAGE_WIDTH,
} from '@ecdlink/ui';
import { useCallback, useEffect, useState } from 'react';
import { useForm, useFormState, useWatch, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { staticDataSelectors } from '@store/static-data';
import * as styles from './styles/smart-space-check-alt';
import { yesNoOptions } from './types/programme-details.types';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import Article from '@/components/article/article';
import { PhotoPrompt } from '@/components/photo-prompt/photo-prompt';
import { traineeSelectors } from '@/store/trainee';
import tool_R4c_form from '@/assets/tool_R4c_form.pdf';
import tool_R4b_form from '@/assets/tool_R4b_form.pdf';
import tool_R4a_form from '@/assets/tool_R4a_form.pdf';
import { useAppDispatch } from '@/store';
import { newGuid } from '@utils/common/uuid.utils';
import { classroomsActions, classroomsThunkActions } from '@/store/classroom';
import { cloneDeep } from 'lodash';
import {
  CoachProgrammeDetailsModel,
  coachProgrammeDetailsSchema,
} from '@/schemas/coach/coach-programme-details';
import { CoachSmartSpaceChecklistProps } from './coach-smart-space-checklist.types';
import { TraineeService } from '@/services/TraineeService';
import { authSelectors } from '@/store/auth';
import {
  CmsVisitSectionInput,
  InputMaybe,
  CmsVisitDataInputModelInput,
  SsChecklistVisitModelInput,
} from '@ecdlink/graphql';
import {
  SectionQuestions,
  Question,
} from '@/pages/trainee/trainee-onboarding/components/smart-space-checklist/smart-space-checklist.types';
import { classroomsForCoachSelectors } from '@/store/classroomForCoach';

export const SmartSpaceCheck4Alt: React.FC<CoachSmartSpaceChecklistProps> = ({
  coachSmartSpaceVisitId,
  traineeChecklistVisitId,
  practitioner,
  handleNextSection,
  saveSmartSpaceCheckData,
}) => {
  const {
    getValues: getProgrammeFormValues,
    setValue: setProgrammeFormValue,
    register: programmeFormRegister,
    trigger: triggerR4bForm,
    control: programmeFormControl,
  } = useForm<CoachProgrammeDetailsModel>({
    resolver: yupResolver(coachProgrammeDetailsSchema),
    shouldUnregister: true,
    mode: 'all',
  });

  const { popiaConsent } = useWatch<CoachProgrammeDetailsModel>({
    control: programmeFormControl,
    defaultValue: {},
  });

  const provinces = useSelector(staticDataSelectors.getProvinces);
  const { isOnline } = useOnlineStatus();
  const { isValid, errors } = useFormState({ control: programmeFormControl });
  const {
    haveTheTitleDeeds,
    ownTheProperty,
    unproclaimedLand,
    liveAtTheProperty,
  } = useWatch<CoachProgrammeDetailsModel>({
    control: programmeFormControl,
    defaultValue: {},
  });
  const [articleTitle, setArticleTitle] = useState<string>();
  const [photoIndex, setPhotoIndex] = useState<number>();
  const [presentArticle, setPresentArticle] = useState<boolean>(false);
  const [contentConsentTypeEnum, setContentConsentTypeEnum] =
    useState<ContentConsentTypeEnum>();
  const [r4bPhotoUrl, setR4bPhotoUrl] = useState<string>();
  const [photoActionBarVisible, setPhotoActionBarVisible] =
    useState<boolean>(false);
  const acceptedFormats = ['jpg, bmp'];

  const visitSection = 'Programme details';
  const visitData = useSelector(
    traineeSelectors.getCoachSmartSpaceSectionAnswers(
      coachSmartSpaceVisitId,
      visitSection
    )
  );
  const [displayPhotoDeleteWarning, setDisplayPhotoDeleteWarning] =
    useState<boolean>(false);

  const classroom = useSelector(
    classroomsForCoachSelectors.getClassroomForCoachByUserId(
      practitioner.userId?.toString() ?? ''
    )
  );

  const userAuth = useSelector(authSelectors.getAuthUser);

  const [sectionQuestions, setSectionQuestions] = useState<SectionQuestions[]>(
    []
  );

  const saveTraineeChecklistData = async () => {
    const sections = sectionQuestions?.map((item) => ({
      ...item,
      questions: item.questions.map((question) => ({
        ...question,
        answer: String(question.answer),
      })),
    })) as InputMaybe<Array<InputMaybe<CmsVisitSectionInput>>>;
    if (traineeChecklistVisitId) {
      const visitDateInput: CmsVisitDataInputModelInput = {
        visitId: traineeChecklistVisitId,
        traineeId: practitioner?.userId,
        visitData: {
          visitName: 'SmartSpace Checklist',
          sections,
        },
      };

      await new TraineeService(userAuth?.auth_token!).editVisitData(
        visitDateInput
      );
      return;
    } else {
      const visitDateInput: SsChecklistVisitModelInput = {
        traineeId: practitioner?.userId,
        attended: true,
        plannedVisitDate: new Date(),
        checklistData: {
          traineeId: practitioner?.userId,
          visitData: {
            visitName: 'SmartSpace Checklist',
            sections,
          },
        },
      };

      await new TraineeService(userAuth?.auth_token!).addSSChecklistForTrainee(
        visitDateInput
      );
    }
  };

  const appDispatch = useAppDispatch();

  const checkedquestion = (question: string | number) => {
    const isChecked = visitData?.find((item) => item?.question === question);
    return isChecked;
  };

  const [questions, setAnswers] = useState(() => {
    // Initialize questions with answers if they exist in visitData
    return [
      {
        visitSection,
        question: `I confirm that ${practitioner.user?.firstName} has read the SmartStarter Information Sheet on Child Protection and Confidentiality`,
        questionAnswer:
          checkedquestion(
            `I confirm that ${practitioner.user?.firstName} has read the SmartStarter Information Sheet on Child Protection and Confidentiality`
          )?.questionAnswer ?? false,
      },
      {
        visitSection,
        question: `By submitting ${practitioner.user?.firstName} information on this site, you consent to the collection, processing and storage of his/her data in accordance with our Privacy Policy and Protection of Personal Information Act (POPIA).`,
        questionAnswer:
          checkedquestion(
            `By submitting ${practitioner.user?.firstName} information on this site, you consent to the collection, processing and storage of his/her data in accordance with our Privacy Policy and Protection of Personal Information Act (POPIA).`
          )?.questionAnswer ?? false,
      },
      {
        visitSection,
        question: `What is the name of ${practitioner.user?.firstName}'s programme?`,
        questionAnswer:
          checkedquestion(
            `What is the name of ${practitioner.user?.firstName}'s programme?`
          )?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: `What type of programme is ${practitioner.user?.firstName} running or planning to run`,
        questionAnswer:
          checkedquestion(
            `What type of programme is ${practitioner.user?.firstName} running or planning to run`
          )?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: 'Ward number',
        questionAnswer: checkedquestion('Ward number')?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: 'Address line 1',
        questionAnswer: checkedquestion('Address line 1')?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: 'Address line 2',
        questionAnswer: checkedquestion('Address line 2')?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: 'City',
        questionAnswer: checkedquestion('City')?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: 'Province',
        questionAnswer: checkedquestion('Province')?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: 'Postal Code',
        questionAnswer: checkedquestion('Postal Code')?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: `Does ${practitioner.user?.firstName} own the property where they will run their SmartStart programme?`,
        questionAnswer:
          checkedquestion(
            `Does ${practitioner.user?.firstName} own the property where they will run their SmartStart programme?`
          )?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: `Does ${practitioner.user?.firstName} have the Title Deeds for the property?`,
        questionAnswer:
          checkedquestion(
            `Does ${practitioner.user?.firstName} have the Title Deeds for the property?`
          )?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: 'Is the property on un-proclaimed land?',
        questionAnswer:
          checkedquestion('Is the property on un-proclaimed land?')
            ?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: 'Take a photo of a certified copy of the Title Deeds',
        questionAnswer:
          checkedquestion('Take a photo of a certified copy of the Title Deeds')
            ?.questionAnswer ?? '',
      },
      {
        visitSection,
        question:
          'Take a photo of the filled and signed R4c Affidavit - Property Ownership',
        questionAnswer:
          checkedquestion(
            'Take a photo of the filled and signed R4c Affidavit - Property Ownership'
          )?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: `Does ${practitioner.user?.firstName} live at the property?`,
        questionAnswer:
          checkedquestion(
            `Does ${practitioner.user?.firstName} live at the property?`
          )?.questionAnswer ?? '',
      },
      {
        visitSection,
        question:
          'Take a photo of the filled and signed R4a form - Agreement for premises to be uesd for early learning programme.',
        questionAnswer:
          checkedquestion(
            'Take a photo of the filled and signed R4a form - Agreement for premises to be uesd for early learning programme.'
          )?.questionAnswer ?? '',
      },
      {
        visitSection,
        question:
          'Take a photo of the filled and signed R4b form - Confirmation of Lease.',
        questionAnswer:
          checkedquestion(
            'Take a photo of the filled and signed R4b form - Confirmation of Lease.'
          )?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: 'Area Classification',
        questionAnswer:
          checkedquestion('Area Classification')?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: 'Area Sub Classification',
        questionAnswer:
          checkedquestion('Area Sub Classification')?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: 'Area',
        questionAnswer: checkedquestion('Area')?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: 'Municipality',
        questionAnswer: checkedquestion('Municipality')?.questionAnswer ?? '',
      },
      {
        visitSection,
        question: 'How many assistants will attend every session?',
        questionAnswer:
          checkedquestion('How many assistants will attend every session?')
            ?.questionAnswer ?? '',
      },
    ];
  });

  const completedItems = visitData
    ?.filter((item) => item?.visitSection === visitSection)
    .filter(
      (item) =>
        item?.questionAnswer === 'true' ||
        (item?.questionAnswer !== ' ' &&
          item?.questionAnswer !== 'false' &&
          item?.questionAnswer !== '')
    );

  const programData = useSelector(staticDataSelectors.getProgrammeTypes);

  const displayArticle = async (key: ContentConsentTypeEnum, title: string) => {
    setContentConsentTypeEnum(key);
    setPresentArticle(true);
    setArticleTitle(title);
  };

  const formatSectionQuestions = (
    questions: {
      visitSection: string;
      question: string;
      questionAnswer: any;
    }[],
    visitSection: string,
    setSectionQuestions: (data: SectionQuestions[]) => void
  ) => {
    const formattedQuestions: Question[] = questions.map(
      ({ question, questionAnswer }) => ({
        question,
        answer: questionAnswer,
      })
    );
    setSectionQuestions([
      {
        visitSection,
        questions: formattedQuestions,
      },
    ]);
  };

  const onOptionSelected = useCallback(
    (value, index) => {
      const currentQuestion = questions[index];

      const updatedQuestions = questions.map((question) => {
        if (question.question === currentQuestion.question) {
          return {
            ...question,
            questionAnswer: value,
          };
        }
        return question;
      });

      setAnswers(updatedQuestions);
      formatSectionQuestions(questions, visitSection, setSectionQuestions);
    },
    [questions]
  );

  const areaClassifications = ['Urban', 'Rural', 'Informal Settlement'];

  const areasSubClassifications = [
    'Metro',
    'City',
    'Town',
    'Village',
    'Informal Settlement',
  ];

  const setPhotoUrl = (imageUrl: string) => {
    setProgrammeFormValue('r4bPhoto', imageUrl);
    onOptionSelected(imageUrl, photoIndex);
    setR4bPhotoUrl(imageUrl);
    triggerR4bForm();
    setPhotoActionBarVisible(false);
  };

  const deletePhoto = () => {
    setProgrammeFormValue('r4bPhoto', '');
    onOptionSelected('', photoIndex);
    setR4bPhotoUrl('');
    setDisplayPhotoDeleteWarning(false);
  };

  const changeSmartSpaceCheckAddress = async () => {
    const classroomCopy = cloneDeep(classroom);
    if (classroomCopy) {
      const programmeInput: ProgrammeTypeDto = {
        enumId: '',
        description: getProgrammeFormValues().programmeType,
      };

      classroomCopy.numberOfAssistants =
        +getProgrammeFormValues().assistants || 0;
      classroomCopy.name = getProgrammeFormValues().programmeName || '';

      await appDispatch(classroomsActions.setProgrammeType(programmeInput));
      await appDispatch(classroomsActions.updateClassroom(classroomCopy));
      if (isOnline) {
        await appDispatch(classroomsThunkActions.upsertClassroom({}));
      }

      // Proceed to update siteAddress if the classroom update succeeded
      const siteAddressId =
        classroomCopy.siteAddressId ||
        classroomCopy.siteAddress?.id ||
        newGuid();

      const siteAddress: SiteAddressDto = {
        id: siteAddressId,
        name: getProgrammeFormValues().programmeName || '',
        areaClassification: getProgrammeFormValues().areaClassification || '',
        areaSubClassification:
          getProgrammeFormValues().areaSubClassification || '',
        addressLine1: getProgrammeFormValues().addressLine1 || '',
        addressLine2: getProgrammeFormValues().addressLine2 || '',
        provinceId: getProgrammeFormValues().provinceId || '',
        municipality: getProgrammeFormValues().municipality || '',
        area: getProgrammeFormValues().area || '',
        addressLine3: getProgrammeFormValues().addressLine3 || '',
        postalCode: getProgrammeFormValues().postalCode || '',
        ward: getProgrammeFormValues().ward || '',
      };

      // The originally used classroom copy cannot be re-used, so another copy is necessary.
      const classroomDeepCopy = {
        ...classroomCopy,
        siteAddress,
        siteAddressId,
      };

      await appDispatch(
        classroomsActions.updateClassroomSiteAddress(
          classroomDeepCopy as ClassroomDto
        )
      );
      if (isOnline) {
        await appDispatch(
          classroomsThunkActions.upsertClassroomSiteAddress(
            classroomDeepCopy as ClassroomDto
          )
        );
      }
    }
  };

  useEffect(() => {
    if (checkedquestion(questions?.[0].question)?.questionAnswer) {
      setProgrammeFormValue('haveReadTheSmartStarterInformation', true);
    }

    if (checkedquestion(questions?.[1].question)?.questionAnswer) {
      setProgrammeFormValue('popiaConsent', true);
    }

    if (checkedquestion(questions?.[2].question)?.questionAnswer) {
      setProgrammeFormValue(
        'programmeName',
        String(checkedquestion(questions?.[2].question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions?.[3].question)?.questionAnswer) {
      setProgrammeFormValue(
        'programmeType',
        String(checkedquestion(questions?.[3].question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions?.[4].question)?.questionAnswer) {
      setProgrammeFormValue(
        'ward',
        String(checkedquestion(questions?.[4].question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions[5].question)?.questionAnswer) {
      setProgrammeFormValue(
        'addressLine1',
        String(checkedquestion(questions[5].question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions[6].question)?.questionAnswer) {
      setProgrammeFormValue(
        'addressLine2',
        String(checkedquestion(questions[6].question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions[7].question)?.questionAnswer) {
      setProgrammeFormValue(
        'addressLine3',
        String(checkedquestion(questions[7].question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions[8].question)?.questionAnswer) {
      setProgrammeFormValue(
        'provinceId',
        String(checkedquestion(questions[8].question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions[9].question)?.questionAnswer) {
      setProgrammeFormValue(
        'postalCode',
        String(checkedquestion(questions[9].question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions?.[10].question)?.questionAnswer) {
      const answer =
        checkedquestion(questions?.[10].question)?.questionAnswer === 'false'
          ? false
          : true;
      setProgrammeFormValue('ownTheProperty', answer);
    }

    if (checkedquestion(questions?.[11]?.question)?.questionAnswer) {
      const answer =
        checkedquestion(questions?.[11].question)?.questionAnswer === 'false'
          ? false
          : true;
      setProgrammeFormValue('haveTheTitleDeeds', answer);
    }

    if (checkedquestion(questions?.[12]?.question)?.questionAnswer) {
      setProgrammeFormValue(
        'unproclaimedLand',
        Boolean(checkedquestion(questions?.[12].question)?.questionAnswer)
      );
    }

    if (checkedquestion(questions?.[13]?.question)?.questionAnswer) {
      setProgrammeFormValue(
        'r4bPhoto',
        String(checkedquestion(questions?.[13]?.question)?.questionAnswer!)
      );

      setR4bPhotoUrl(
        String(checkedquestion(questions?.[13]?.question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions?.[14]?.question)?.questionAnswer) {
      setProgrammeFormValue(
        'r4bPhoto',
        String(checkedquestion(questions?.[14]?.question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions?.[15]?.question)?.questionAnswer) {
      const answer =
        checkedquestion(questions?.[15].question)?.questionAnswer === 'false'
          ? false
          : true;
      setProgrammeFormValue('liveAtTheProperty', answer);
    }

    if (checkedquestion(questions?.[16]?.question)?.questionAnswer) {
      setProgrammeFormValue(
        'r4bPhoto',
        String(checkedquestion(questions?.[16].question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions?.[17]?.question)?.questionAnswer) {
      setProgrammeFormValue(
        'r4bPhoto',
        String(checkedquestion(questions?.[17]?.question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions?.[18]?.question)?.questionAnswer) {
      setProgrammeFormValue(
        'areaClassification',
        String(checkedquestion(questions?.[18].question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions?.[19]?.question)?.questionAnswer) {
      setProgrammeFormValue(
        'areaSubClassification',
        String(checkedquestion(questions?.[19].question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions?.[20]?.question)?.questionAnswer) {
      setProgrammeFormValue(
        'area',
        String(checkedquestion(questions?.[20].question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions?.[21]?.question)?.questionAnswer) {
      setProgrammeFormValue(
        'municipality',
        String(checkedquestion(questions?.[21].question)?.questionAnswer!)
      );
    }

    if (checkedquestion(questions?.[22]?.question)?.questionAnswer) {
      setProgrammeFormValue(
        'assistants',
        checkedquestion(questions?.[22].question)?.questionAnswer!
      );
    }
  }, []);

  useEffect(() => {
    if (ownTheProperty === false) {
      setProgrammeFormValue('haveTheTitleDeeds', false);
    }
  }, [ownTheProperty, setProgrammeFormValue]);

  useEffect(() => {
    formatSectionQuestions(questions, visitSection, setSectionQuestions);
  }, [questions]);

  useEffect(() => {
    if (ownTheProperty === true) {
      setProgrammeFormValue('liveAtTheProperty', undefined);
    }
  }, [ownTheProperty, setProgrammeFormValue]);

  const onDownloadImageR4c = () => {
    const pdfUrl = tool_R4c_form;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('download', 'r4b_form.pdf');
    document.body.appendChild(link);
    link.click();
  };

  const onDownloadImageR4b = () => {
    const pdfUrl = tool_R4b_form;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('download', 'r4b_form.pdf');
    document.body.appendChild(link);
    link.click();
  };

  const onDownloadImageR4a = () => {
    const pdfUrl = tool_R4a_form;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.setAttribute('download', 'r4a_form.pdf');
    document.body.appendChild(link);
    link.click();
  };

  const [formattedQuestions, setFormattedQuestions] = useState<any[]>([]);

  useEffect(() => {
    if (questions && questions.length > 0) {
      const formatted = questions.map((q) => ({
        ...q,
        questionAnswer: String(q.questionAnswer),
      }));
      setFormattedQuestions(formatted);
    }
  }, [questions]);

  return (
    <>
      <div
        className={`p-4 ${
          Boolean(checkedquestion(questions?.[0].question)) ? '' : ''
        }`}
      >
        <Typography
          type={'h2'}
          text={'Programme details'}
          color={'textDark'}
          className={'my-3'}
        />

        {/* {disableSection && (
          <Alert
            className="my-4"
            type="warning"
            title="You are viewing this form and cannot edit responses."
            list={['This form should be filled in by the trainee.']}
          />
        )} */}
        <Typography
          type={'h4'}
          text={'Child protection and confidentiality'}
          color={'textDark'}
          className={'my-3'}
        />
        <div className="flex items-center">
          <div className="'flex items-center' w-full flex-row justify-start">
            <div
              className="flex items-start gap-2"
              onClick={() => {
                setProgrammeFormValue(
                  'haveReadTheSmartStarterInformation',
                  !getProgrammeFormValues().haveReadTheSmartStarterInformation
                );
                onOptionSelected(
                  getProgrammeFormValues().haveReadTheSmartStarterInformation,
                  0
                );
              }}
            >
              <Checkbox<CoachProgrammeDetailsModel>
                checked={
                  Boolean(
                    checkedquestion(questions?.[0].question)?.questionAnswer
                  ) ||
                  getProgrammeFormValues().haveReadTheSmartStarterInformation
                }
                register={programmeFormRegister}
                nameProp={'haveReadTheSmartStarterInformation'}
                onCheckboxChange={() =>
                  onOptionSelected(
                    getProgrammeFormValues().haveReadTheSmartStarterInformation,
                    0
                  )
                }
                value={
                  String(
                    getProgrammeFormValues().haveReadTheSmartStarterInformation
                  ) || checkedquestion(questions?.[0].question)?.questionAnswer!
                }
              ></Checkbox>
              <Typography
                text={questions?.[0].question}
                type="body"
                color={'textMid'}
              />
            </div>
          </div>

          <Typography
            type={'body'}
            text={'View'}
            color={'secondary'}
            className={'my-3'}
            underline={true}
            onClick={() => {
              displayArticle(
                ContentConsentTypeEnum.ChildProtection,
                'Child Protection'
              );
            }}
          />
        </div>

        <Typography
          type={'h4'}
          text={'Protection of Personal Information Act, 4 of 2023 (POPIA)'}
          color={'textDark'}
          className={'my-3'}
        />

        <div className="flex items-center">
          <div className="'flex items-center' w-full flex-row justify-start">
            <div
              className="flex items-start gap-2"
              onClick={() => {
                setProgrammeFormValue('popiaConsent', !popiaConsent);
                onOptionSelected(getProgrammeFormValues().popiaConsent, 1);
              }}
            >
              <Checkbox<CoachProgrammeDetailsModel>
                checked={
                  Boolean(
                    checkedquestion(questions?.[1].question)?.questionAnswer
                  ) || getProgrammeFormValues().popiaConsent
                }
                register={programmeFormRegister}
                nameProp={'popiaConsent'}
                onCheckboxChange={() =>
                  onOptionSelected(getProgrammeFormValues().popiaConsent, 1)
                }
                value={String(getProgrammeFormValues().popiaConsent)}
                disabled={Boolean(checkedquestion(questions?.[1].question))}
              ></Checkbox>
              <Typography
                text={questions?.[1].question}
                type="body"
                color={'textMid'}
              />
            </div>
          </div>

          <Typography
            type={'body'}
            text={'View'}
            color={'secondary'}
            className={'my-3'}
            underline={true}
            onClick={() => {
              displayArticle(ContentConsentTypeEnum.POPIA, 'POPIA Consent');
            }}
          />
        </div>
      </div>
      <div className="mt-2 space-y-4 p-4">
        <>
          <FormInput<CoachProgrammeDetailsModel>
            label={questions?.[2].question}
            error={errors['programmeName']}
            register={programmeFormRegister}
            nameProp={'programmeName'}
            placeholder={'E.g. Little Lambs Preschool'}
            type={'text'}
            onChange={(e) => {
              var value = (e.target as HTMLInputElement).value;
              setProgrammeFormValue('programmeName', value);
              onOptionSelected(value, 2);
            }}
            //disabled={Boolean(checkedquestion(questions?.[1].question))}
          ></FormInput>
          <div className={`w-full`}>
            <label className={styles.label}>{questions?.[3].question}</label>
            <div className="mt-1">
              <Controller
                name={'programmeType'}
                control={programmeFormControl}
                render={({ field: { onChange, value, ref } }) => (
                  <ButtonGroup<string | string[]>
                    inputRef={ref}
                    options={
                      (programData &&
                        programData.map((x: ProgrammeTypeDto) => {
                          return { text: x.description, value: x.id ?? '' };
                        })) ||
                      []
                    }
                    multiple={false}
                    onOptionSelected={(e) => {
                      setProgrammeFormValue('programmeType', e as string);
                      onOptionSelected(e, 3);
                    }}
                    selectedOptions={value}
                    color="secondary"
                    type={ButtonGroupTypes.Button}
                    className={'w-full'}
                  />
                )}
              ></Controller>
            </div>
          </div>
          <div className={``}>
            <Dropdown
              placeholder={'Area Classification'}
              list={
                (areaClassifications &&
                  areaClassifications.map((area: string) => ({
                    label: area,
                    value: area || '',
                  }))) ||
                []
              }
              fillType="clear"
              fullWidth={true}
              label={'Area Classification'}
              className={classNames(styles.divider, 'w-full')}
              selectedValue={getProgrammeFormValues().areaClassification}
              onChange={(value: string) => {
                setProgrammeFormValue('areaClassification', value);

                setAnswers((prevQuestions) =>
                  prevQuestions.map((q) =>
                    q.question === 'Area Classification'
                      ? { ...q, questionAnswer: value }
                      : q
                  )
                );
              }}
            />

            <Dropdown
              placeholder={'Area Sub Classification'}
              list={
                (areasSubClassifications &&
                  areasSubClassifications.map((area: string) => ({
                    label: area,
                    value: area || '',
                  }))) ||
                []
              }
              fillType="clear"
              fullWidth={true}
              label={'Area Sub Classification'}
              className={classNames(styles.divider, 'w-full')}
              selectedValue={getProgrammeFormValues().areaSubClassification}
              onChange={(value: string) => {
                setProgrammeFormValue('areaSubClassification', value);

                // Update the questions array
                setAnswers((prevQuestions) =>
                  prevQuestions.map((q) =>
                    q.question === 'Area Sub Classification'
                      ? { ...q, questionAnswer: value }
                      : q
                  )
                );
              }}
            />

            <div className="mt-4">
              <FormInput<CoachProgrammeDetailsModel>
                label={questions?.[5].question}
                error={errors['addressLine1']}
                register={programmeFormRegister}
                nameProp={'addressLine1'}
                placeholder={'E.g. 11 Green Road'}
                type={'text'}
                onChange={(e) => {
                  var value = (e.target as HTMLInputElement).value;
                  setProgrammeFormValue('addressLine1', value);
                  onOptionSelected(value, 5);
                }}
                //disabled={Boolean(checkedquestion(questions?.[4].question))}
              ></FormInput>
            </div>

            <div className="mt-4">
              <FormInput<CoachProgrammeDetailsModel>
                label={questions?.[6].question}
                error={errors['addressLine2']}
                register={programmeFormRegister}
                nameProp={'addressLine2'}
                placeholder={'E.g. Mamelodi East'}
                type={'text'}
                onChange={(e) => {
                  var value = (e.target as HTMLInputElement).value;
                  setProgrammeFormValue('addressLine2', value);
                  onOptionSelected(value, 6);
                }}
                //disabled={Boolean(checkedquestion(questions?.[5].question))}
              ></FormInput>
            </div>

            <Dropdown
              placeholder={'Choose province'}
              list={
                (provinces &&
                  provinces.map((province: ProvinceDto) => ({
                    label: province.description,
                    value: province.id || '',
                  }))) ||
                []
              }
              fillType="clear"
              fullWidth={true}
              label={'Province'}
              className={classNames(styles.divider, 'w-full')}
              selectedValue={getProgrammeFormValues().provinceId}
              onChange={(value: string) => {
                setProgrammeFormValue('provinceId', value);
                onOptionSelected(value, 8);
              }}
            />

            <div className="mt-4">
              <FormInput<CoachProgrammeDetailsModel>
                label={questions?.[21].question}
                error={errors['municipality']}
                register={programmeFormRegister}
                nameProp={'municipality'}
                placeholder={'e.g. City of Cape Town Metropolitan'}
                type={'text'}
                onChange={(e) => {
                  var value = (e.target as HTMLInputElement).value;
                  setProgrammeFormValue('municipality', value);
                  onOptionSelected(value, 21);
                }}
              ></FormInput>
            </div>

            <div className="mt-4">
              <FormInput<CoachProgrammeDetailsModel>
                label={questions?.[20].question}
                error={errors['area']}
                register={programmeFormRegister}
                nameProp={'area'}
                placeholder={'e.g. Bellville'}
                type={'text'}
                onChange={(e) => {
                  var value = (e.target as HTMLInputElement).value;
                  setProgrammeFormValue('area', value);
                  onOptionSelected(value, 20);
                }}
              ></FormInput>
            </div>

            <div className="mt-4">
              <FormInput<CoachProgrammeDetailsModel>
                label={questions?.[7].question}
                register={programmeFormRegister}
                error={errors['addressLine3']}
                nameProp={'addressLine3'}
                placeholder={'E.g. Cape Town'}
                type={'text'}
                onChange={(e) => {
                  var value = (e.target as HTMLInputElement).value;
                  setProgrammeFormValue('addressLine3', value);
                  onOptionSelected(value, 7);
                }}
                //disabled={Boolean(checkedquestion(questions?.[6].question))}
              ></FormInput>
            </div>

            <div className="mt-4">
              <FormInput<CoachProgrammeDetailsModel>
                label={questions?.[9].question}
                error={errors['postalCode']}
                register={programmeFormRegister}
                nameProp={'postalCode'}
                placeholder={'E.g. 0081'}
                type={'text'}
                onChange={(e) => {
                  var value = (e.target as HTMLInputElement).value;
                  setProgrammeFormValue('postalCode', value);
                  onOptionSelected(value, 9);
                }}
                //disabled={Boolean(checkedquestion(questions?.[8].question))}
              ></FormInput>
            </div>
          </div>

          <div className="mt-4">
            <FormInput<CoachProgrammeDetailsModel>
              label={questions?.[4].question}
              error={errors['ward']}
              register={programmeFormRegister}
              nameProp={'ward'}
              placeholder={'e.g. 0721'}
              type={'text'}
              onChange={(e) => {
                var value = (e.target as HTMLInputElement).value;
                setProgrammeFormValue('ward', value);
                onOptionSelected(value, 4);
              }}
              //disabled={Boolean(checkedquestion(questions?.[3].question))}
            ></FormInput>
          </div>

          <div className={'w-full'}>
            <label className={styles.label}>{questions?.[10].question}</label>
            <div>
              <ButtonGroup<boolean | undefined>
                options={yesNoOptions}
                onOptionSelected={(value: any) => {
                  setProgrammeFormValue('ownTheProperty', value as boolean, {
                    shouldValidate: true,
                  });
                  onOptionSelected(value, 10);
                  deletePhoto();
                }}
                selectedOptions={[getProgrammeFormValues().ownTheProperty]}
                color="secondary"
                type={ButtonGroupTypes.Button}
                className={'w-full'}
              />
            </div>
          </div>

          {ownTheProperty === true && (
            <div className={'w-full'}>
              <label className={styles.label}>{questions?.[11].question}</label>
              <div className={``}>
                <ButtonGroup<boolean | undefined>
                  options={yesNoOptions}
                  onOptionSelected={(value: any) => {
                    setProgrammeFormValue(
                      'haveTheTitleDeeds',
                      value as boolean,
                      {
                        shouldValidate: true,
                      }
                    );
                    onOptionSelected(value, 11);
                    deletePhoto();
                  }}
                  selectedOptions={[getProgrammeFormValues().haveTheTitleDeeds]}
                  color="secondary"
                  type={ButtonGroupTypes.Button}
                  className={'w-full'}
                />
              </div>
            </div>
          )}
          {ownTheProperty === false && (
            <div className={'w-full'}>
              <label className={styles.label}>{questions?.[15].question}</label>
              <div className={``}>
                <ButtonGroup<boolean | undefined>
                  options={yesNoOptions}
                  onOptionSelected={(value: any) => {
                    setProgrammeFormValue(
                      'liveAtTheProperty',
                      value as boolean,
                      {
                        shouldValidate: true,
                      }
                    );
                    onOptionSelected(value, 10);
                    deletePhoto();
                  }}
                  selectedOptions={[getProgrammeFormValues().liveAtTheProperty]}
                  color="secondary"
                  type={ButtonGroupTypes.Button}
                  className={'w-full'}
                />
              </div>
            </div>
          )}
          {liveAtTheProperty === true && (
            <>
              <Alert
                className="mb-4"
                type="info"
                title={`The owner or landlord of the property will need to fill in the “confirmation of lease” with ${practitioner.user?.firstName} (form R4b).`}
                list={[
                  `${practitioner.user?.firstName} must fill in the form with their landlord and upload a picture of it below.`,
                ]}
                button={
                  <Button
                    onClick={onDownloadImageR4b}
                    text="Download the R4b form"
                    icon="DownloadIcon"
                    type={'filled'}
                    color={'primary'}
                    textColor={'white'}
                  />
                }
              />
              <ImageInput<CoachProgrammeDetailsModel>
                acceptedFormats={acceptedFormats}
                resolutionLimit={IMAGE_WIDTH}
                label={questions?.[17].question}
                nameProp="r4bPhoto"
                icon="CameraIcon"
                className={'py-4'}
                currentImageString={r4bPhotoUrl}
                register={programmeFormRegister}
                overrideOnClick={() => {
                  setPhotoIndex(17);
                  setPhotoActionBarVisible(true);
                }}
                //disabled={Boolean(checkedquestion(questions?.[16].question))}
              ></ImageInput>
            </>
          )}
          {liveAtTheProperty === false && (
            <div className={''}>
              <Alert
                className="mb-4"
                type="info"
                title={`The owner or landlord of the property will need to fill in the “Agreement for premises to be used for early learning programme” with ${practitioner.user?.firstName} (form 4a).`}
                list={[
                  `Fill in the form with ${practitioner.user?.firstName}'s landlord and upload a picture of it below.`,
                ]}
                button={
                  <Button
                    onClick={onDownloadImageR4a}
                    text="Download the R4a form"
                    icon="DownloadIcon"
                    type={'filled'}
                    color={'primary'}
                    textColor={'white'}
                  />
                }
              />
              <ImageInput<CoachProgrammeDetailsModel>
                acceptedFormats={acceptedFormats}
                resolutionLimit={IMAGE_WIDTH}
                label={questions?.[16].question}
                nameProp="r4bPhoto"
                icon="CameraIcon"
                className={'py-4'}
                currentImageString={r4bPhotoUrl}
                register={programmeFormRegister}
                overrideOnClick={() => {
                  setPhotoIndex(16);
                  setPhotoActionBarVisible(true);
                }}
                //disabled={Boolean(checkedquestion(questions?.[15].question))}
              ></ImageInput>
            </div>
          )}
        </>
        {haveTheTitleDeeds === false && ownTheProperty === true && (
          <div className={'w-full'}>
            <label className={styles.label}>{questions?.[12].question}</label>
            <div className={``}>
              <Controller
                name="unproclaimedLand"
                control={programmeFormControl}
                render={({ field: { onChange, value, ref } }) => (
                  <ButtonGroup<boolean>
                    inputRef={ref}
                    options={yesNoOptions}
                    onOptionSelected={(value: any) => {
                      setProgrammeFormValue(
                        'unproclaimedLand',
                        value as boolean,
                        {
                          shouldValidate: true,
                        }
                      );
                      onOptionSelected(value, 7);
                      deletePhoto();
                    }}
                    selectedOptions={value}
                    color="secondary"
                    type={ButtonGroupTypes.Button}
                    className={'w-full'}
                  />
                )}
              />
            </div>
          </div>
        )}

        {haveTheTitleDeeds === true && (
          <ImageInput<CoachProgrammeDetailsModel>
            acceptedFormats={acceptedFormats}
            resolutionLimit={IMAGE_WIDTH}
            label={questions?.[13].question}
            nameProp="r4bPhoto"
            icon="CameraIcon"
            className={'py-4'}
            currentImageString={r4bPhotoUrl}
            register={programmeFormRegister}
            overrideOnClick={() => {
              setPhotoIndex(13);
              setPhotoActionBarVisible(true);
            }}
            //disabled={Boolean(checkedquestion(questions?.[12].question))}
          ></ImageInput>
        )}

        {unproclaimedLand === false && (
          <Alert
            className="mb-4"
            type="warning"
            title={`Ask ${practitioner.user?.firstName} to apply to their municipality for a copy of the title deeds`}
            list={[
              `${practitioner.user?.firstName} will need to get a copy of the title deeds in order to complete this section.`,
              `Once ${practitioner.user?.firstName} have the title deeds, answer “Yes” to the Title Deeds question above and add a photo of the document.`,
            ]}
          />
        )}

        {unproclaimedLand === true && (
          <div className={''}>
            <Alert
              className="mb-4"
              type="info"
              title={`Please get a signed, stamped copy of an R4c form instead.`}
              list={[
                'This form can be stamped by any commissioner of oaths. This could include: police officers, South African Post Office workers, lawyers, or accountants for example.',
              ]}
              button={
                <Button
                  onClick={onDownloadImageR4c}
                  text="Download the R4c form"
                  icon="DownloadIcon"
                  type={'filled'}
                  color={'primary'}
                  textColor={'white'}
                />
              }
            />
            <ImageInput<CoachProgrammeDetailsModel>
              acceptedFormats={acceptedFormats}
              resolutionLimit={IMAGE_WIDTH}
              label={questions?.[14].question}
              nameProp="r4bPhoto"
              icon="CameraIcon"
              className={'py-4'}
              currentImageString={r4bPhotoUrl}
              register={programmeFormRegister}
              overrideOnClick={() => {
                setPhotoIndex(14);
                setPhotoActionBarVisible(true);
              }}
            ></ImageInput>
          </div>
        )}
        <div>
          <div className="mt-4">
            <FormInput<CoachProgrammeDetailsModel>
              label={questions?.[22].question}
              error={errors['assistants']}
              register={programmeFormRegister}
              nameProp={'assistants'}
              placeholder={'e.g. 2'}
              type={'text'}
              onChange={(e) => {
                var value = (e.target as HTMLInputElement).value;
                setProgrammeFormValue('assistants', value);
                onOptionSelected(value, 22);
              }}
              //disabled={Boolean(checkedquestion(questions?.[3].question))}
            ></FormInput>
          </div>
          <div>
            <Button
              type="filled"
              color="primary"
              className={styles.button}
              disabled={
                !isValid ||
                Boolean(
                  !getProgrammeFormValues().haveReadTheSmartStarterInformation
                )
              }
              onClick={() => {
                saveTraineeChecklistData();
                changeSmartSpaceCheckAddress();
                saveSmartSpaceCheckData(formattedQuestions, visitSection);
                handleNextSection();
              }}
            >
              {renderIcon('ArrowCircleRightIcon', 'mr-2 text-white w-5')}
              <Typography type={'help'} text={'Next'} color={'white'} />
            </Button>
          </div>
        </div>
      </div>
      {contentConsentTypeEnum && (
        <Article
          consentEnumType={contentConsentTypeEnum}
          visible={presentArticle}
          title={articleTitle}
          onClose={() => setPresentArticle(false)}
          isOpen={true}
        />
      )}
      <Dialog
        visible={photoActionBarVisible}
        position={DialogPosition.Bottom}
        stretch
      >
        <div>
          <PhotoPrompt
            title={'Upload image'}
            resolutionLimit={IMAGE_WIDTH}
            onClose={() => setPhotoActionBarVisible(false)}
            onAction={(imageUrl: string) => {
              setPhotoUrl(imageUrl);
            }}
            onDelete={() => deletePhoto()}
          ></PhotoPrompt>
        </div>
      </Dialog>
    </>
  );
};
